import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ArrowLeft, Send, Save, Paperclip, Upload, X, FileText, File as FileIcon, Image as ImageIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

export default function RequestForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [departments, setDepartments] = useState([]);
  const [sponsorOrgs, setSponsorOrgs] = useState([]);
  const [surveyOptions, setSurveyOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [form, setForm] = useState({
    visitor_name: "",
    visitor_email: "",
    specialty: "",
    department: "",
    arrival_date: null,
    departure_date: null,
    party_size: 1,
    special_needs: "",
    purpose: "",
    is_student: false,
    is_resident: false,
    sponsor_org_id: "",
    survey_ids: [],
  });

  useEffect(() => {
    api.get("/departments").then((res) => setDepartments(res.data)).catch(() => {});
    api.get("/sponsor-orgs").then((res) => setSponsorOrgs(res.data || [])).catch(() => setSponsorOrgs([]));
    api.get("/surveys/options").then((res) => setSurveyOptions(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEditMode) {
      const fetchRequest = async () => {
        try {
          const { data } = await api.get(`/requests/${id}`);
          setForm({
            visitor_name: data.visitor_name || "",
            visitor_email: data.visitor_email || "",
            specialty: data.specialty || "",
            department: data.department || "",
            arrival_date: data.arrival_date ? parseISO(data.arrival_date) : null,
            departure_date: data.departure_date ? parseISO(data.departure_date) : null,
            party_size: data.party_size || 1,
            special_needs: data.special_needs || "",
            purpose: data.purpose || "",
            is_student: !!data.is_student,
            is_resident: !!data.is_resident,
            sponsor_org_id: data.sponsor_org_id || "",
          });
        } catch {
          toast.error("Failed to load request");
          navigate("/dashboard");
        } finally {
          setLoading(false);
        }
      };
      fetchRequest();
    }
  }, [id, isEditMode, navigate]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Phase 59.2 — attach files during creation. Held in state until the
  // request is POSTed, then uploaded to /documents/upload against the new id.
  const [pendingFiles, setPendingFiles] = useState([]);
  const addPendingFiles = (fileList) => {
    const chosen = Array.from(fileList || []);
    const valid = chosen.filter((f) => {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 10 MB — skipped`);
        return false;
      }
      return true;
    });
    setPendingFiles((prev) => [...prev, ...valid]);
  };
  const removePendingFile = (idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const uploadPendingAttachments = async (requestId) => {
    for (const f of pendingFiles) {
      try {
        const form = new FormData();
        form.append("entity_type", "request");
        form.append("entity_id", requestId);
        form.append("file", f);
        await api.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.visitor_name || !form.specialty || !form.department || !form.arrival_date || !form.departure_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        arrival_date: format(form.arrival_date, "yyyy-MM-dd"),
        departure_date: format(form.departure_date, "yyyy-MM-dd"),
      };
      if (isEditMode) {
        await api.put(`/requests/${id}/edit`, payload);
        if (pendingFiles.length > 0) await uploadPendingAttachments(id);
        toast.success("Request updated successfully");
        navigate(`/requests/${id}`);
      } else {
        const { data: created } = await api.post("/requests", payload);
        if (pendingFiles.length > 0 && created?.id) {
          await uploadPendingAttachments(created.id);
        }
        toast.success(pendingFiles.length > 0
          ? `Request submitted with ${pendingFiles.length} attachment(s)`
          : "Request submitted successfully");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto fade-in" data-testid="request-form-page">
      <Button variant="ghost" onClick={() => navigate(isEditMode ? `/requests/${id}` : "/dashboard")} className="mb-6 text-[#525C58] hover:text-[#1C1F1E]">
        <ArrowLeft className="h-4 w-4 mr-2" /> {isEditMode ? "Back to Request" : "Back to Dashboard"}
      </Button>

      <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {isEditMode ? "Edit Request" : "New Visitor Request"}
      </h1>
      <p className="text-[#525C58] mb-8">
        {isEditMode ? "Update the visitor request details" : "Submit a request for a visiting health professional"}
      </p>

      <Card className="border-[#E8E8E6]">
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="request-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Visitor Name *</Label>
                <Input
                  value={form.visitor_name}
                  onChange={(e) => updateField("visitor_name", e.target.value)}
                  placeholder="Full name"
                  required
                  data-testid="visitor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Visitor Email</Label>
                <Input
                  type="email"
                  value={form.visitor_email}
                  onChange={(e) => updateField("visitor_email", e.target.value)}
                  placeholder="email@example.com"
                  data-testid="visitor-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Specialty *</Label>
                <Input
                  value={form.specialty}
                  onChange={(e) => updateField("specialty", e.target.value)}
                  placeholder="e.g. Orthopedic Surgery"
                  required
                  data-testid="specialty-input"
                />
                <div className="flex items-center gap-4 pt-1.5">
                  <label className="flex items-center gap-2 text-sm text-[#1C1F1E] cursor-pointer select-none" data-testid="is-student-toggle">
                    <input
                      type="checkbox"
                      checked={!!form.is_student}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({ ...prev, is_student: checked, is_resident: checked ? false : prev.is_resident }));
                      }}
                      className="h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                      data-testid="is-student-checkbox"
                    />
                    Student
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#1C1F1E] cursor-pointer select-none" data-testid="is-resident-toggle">
                    <input
                      type="checkbox"
                      checked={!!form.is_resident}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({ ...prev, is_resident: checked, is_student: checked ? false : prev.is_student }));
                      }}
                      className="h-4 w-4 rounded border-[#7A8A84] text-[#1B4332] focus:ring-[#1B4332]"
                      data-testid="is-resident-checkbox"
                    />
                    Resident
                  </label>
                  <span className="text-xs text-[#7A8A84]">(leave both unchecked for Faculty)</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Department *</Label>
                <Select value={form.department} onValueChange={(v) => updateField("department", v)}>
                  <SelectTrigger data-testid="department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => (
                      <SelectItem key={d.id || d} value={d.key || d}>{d.name || d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Phase 58 — sending sponsor (optional) */}
            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">Sending organisation <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
              <Select value={form.sponsor_org_id || "none"} onValueChange={(v) => updateField("sponsor_org_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sponsor-org-select">
                  <SelectValue placeholder="No sponsor selected" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {sponsorOrgs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sponsorOrgs.length === 0 && (
                <p className="text-xs text-[#7A8A84]">No sponsor orgs yet — add some under <strong>Sponsor Orgs</strong>.</p>
              )}
            </div>

            {/* Phase 67.2 — post-trip surveys (optional, multiple allowed) */}
            {surveyOptions.length > 0 && (
              <div className="space-y-2" data-testid="request-survey-picker">
                <Label className="text-[#1C1F1E]">Include post-trip survey <span className="text-[#7A8A84] font-normal">(optional — sent automatically after the stay ends)</span></Label>
                <div className="flex flex-col gap-1 border border-[#E8E8E6] rounded-lg p-2">
                  {surveyOptions.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 rounded hover:bg-[#F4F4F1]">
                      <input
                        type="checkbox"
                        checked={form.survey_ids.includes(s.id)}
                        onChange={() => updateField("survey_ids", form.survey_ids.includes(s.id)
                          ? form.survey_ids.filter((x) => x !== s.id)
                          : [...form.survey_ids, s.id])}
                        data-testid={`request-survey-${s.id}`}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Arrival Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal border-[#E8E8E6]"
                      data-testid="arrival-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#7A8A84]" />
                      {form.arrival_date ? format(form.arrival_date, "PPP") : <span className="text-[#7A8A84]">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.arrival_date} onSelect={(d) => updateField("arrival_date", d)} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Departure Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal border-[#E8E8E6]"
                      data-testid="departure-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#7A8A84]" />
                      {form.departure_date ? format(form.departure_date, "PPP") : <span className="text-[#7A8A84]">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.departure_date} onSelect={(d) => updateField("departure_date", d)} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#1C1F1E]">Party Size</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.party_size}
                  onChange={(e) => updateField("party_size", parseInt(e.target.value) || 1)}
                  data-testid="party-size-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">Purpose of Visit</Label>
              <Textarea
                value={form.purpose}
                onChange={(e) => updateField("purpose", e.target.value)}
                placeholder="Describe the purpose of the visit"
                rows={3}
                data-testid="purpose-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">Special Needs / Accommodations</Label>
              <Textarea
                value={form.special_needs}
                onChange={(e) => updateField("special_needs", e.target.value)}
                placeholder="Any dietary, accessibility, or other requirements"
                rows={3}
                data-testid="special-needs-input"
              />
            </div>

            {/* Phase 59.2 — attach documents (medical licence, CV, passport) */}
            <div className="space-y-2" data-testid="request-attachments-section">
              <div className="flex items-center justify-between">
                <Label className="text-[#1C1F1E] flex items-center gap-1.5">
                  <Paperclip className="h-4 w-4 text-[#7A8A84]" />
                  Attachments <span className="text-[#7A8A84] font-normal">(optional)</span>
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => { addPendingFiles(e.target.files); e.target.value = ""; }}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic"
                    data-testid="request-attachment-input"
                  />
                  <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border bg-white text-[#1B4332] border-[#1B4332]/30 hover:bg-[#1B4332]/5">
                    <Upload className="h-3 w-3" /> Choose files
                  </span>
                </label>
              </div>
              {pendingFiles.length === 0 ? (
                <p className="text-[11px] text-[#7A8A84] italic">
                  PDF · Word · Excel · images (10 MB max). Files upload once the request is submitted.
                </p>
              ) : (
                <div className="space-y-1">
                  {pendingFiles.map((f, idx) => {
                    const ext = (f.name.split(".").pop() || "").toLowerCase();
                    const Icon = ["png","jpg","jpeg","webp","heic"].includes(ext)
                      ? ImageIcon
                      : ["pdf","doc","docx","xls","xlsx"].includes(ext) ? FileText : FileIcon;
                    return (
                      <div key={`${f.name}-${idx}`} className="flex items-center gap-2 rounded border border-[#E8E8E6] bg-white px-2 py-1.5 text-xs"
                           data-testid={`pending-attachment-${idx}`}>
                        <Icon className="h-4 w-4 text-[#7A8A84]" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-[#1C1F1E]" title={f.name}>{f.name}</div>
                          <div className="text-[10px] text-[#7A8A84]">
                            {f.size < 1024 ? `${f.size} B` : f.size < 1024*1024 ? `${(f.size/1024).toFixed(1)} KB` : `${(f.size/(1024*1024)).toFixed(1)} MB`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePendingFile(idx)}
                          className="p-1 rounded hover:bg-red-50 text-red-600"
                          title="Remove"
                          data-testid={`remove-pending-attachment-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="bg-[#1B4332] hover:bg-[#2D6A4F] transition-colors duration-200"
                disabled={submitting}
                data-testid="submit-request-btn"
              >
                {submitting ? "Saving..." : isEditMode ? (
                  <><Save className="h-4 w-4 mr-2" /> Update Request</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Submit Request</>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(isEditMode ? `/requests/${id}` : "/dashboard")}
                className="border-[#E8E8E6]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
